import { useEffect, useMemo, useState } from "react";
import { Star } from "lucide-react";
import type { Need } from "@hub/shared";
import { Button, Field, Input, Modal, Select, Textarea, toast } from "@/ui";
import { useEngineQuery } from "../_shims";
import { useCompleteNeed } from "../lib/hooks";
import s from "../network.module.css";

type Method = "hub" | "outside" | "no_longer_needed";

const METHODS: { value: Method; label: string; hint: string }[] = [
  { value: "hub", label: "Someone from Hub helped", hint: "Pick who helped; a rating thanks them." },
  { value: "outside", label: "Solved outside Hub", hint: "You found help elsewhere." },
  { value: "no_longer_needed", label: "No longer needed", hint: "Closes the need without a helper." },
];

export function CompleteNeedDialog({ need, open, onClose, matchId }: { need: Need | null; open: boolean; onClose: () => void; matchId?: string | null }) {
  const [method, setMethod] = useState<Method>("hub");
  const [helperQuery, setHelperQuery] = useState("");
  const [helperId, setHelperId] = useState<string | null>(null);
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5 | null>(null);
  const [review, setReview] = useState("");
  const complete = useCompleteNeed();
  const people = useEngineQuery("queryContacts", { query: helperQuery, limit: 8, sort: "score", direction: "desc" }, { enabled: open && method === "hub" });

  useEffect(() => {
    if (!open) return;
    setMethod("hub");
    setHelperQuery("");
    setHelperId(null);
    setRating(null);
    setReview("");
  }, [open, need?.id]);

  const helperName = useMemo(() => people.data?.items.find((p) => p.contactId === helperId)?.displayName ?? null, [people.data, helperId]);
  const needsHelper = method === "hub" && !helperId;

  const submit = async () => {
    if (!need) return;
    try {
      await complete.mutateAsync({ needId: need.id, method, helperContactId: method === "hub" ? helperId : null, matchId: matchId ?? null, rating: method === "hub" ? rating : null, reviewText: review.trim() || null });
      toast("Need completed.", "success");
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not complete the need.", "danger");
    }
  };

  return (
    <Modal
      open={open && Boolean(need)}
      onClose={onClose}
      title={`Complete: ${need?.title ?? ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={complete.isPending} disabled={needsHelper}>
            Mark complete
          </Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }}>
        <Field label="How was it resolved?" hint={METHODS.find((m) => m.value === method)?.hint}>
          <Select value={method} onChange={(e) => setMethod(e.target.value as Method)} aria-label="Method">
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        </Field>
        {method === "hub" && (
          <>
            <Field label="Who helped?" hint={helperName ? `Selected: ${helperName}` : "Search your people."}>
              <Input value={helperQuery} onChange={(e) => setHelperQuery(e.target.value)} placeholder="Type a name" aria-label="Helper search" />
            </Field>
            <div className={s.chips} role="listbox" aria-label="Helper candidates">
              {(people.data?.items ?? []).map((p) => (
                <button key={p.contactId} type="button" role="option" aria-selected={helperId === p.contactId} className={s.chip} data-selectable="true" data-on={helperId === p.contactId ? "true" : undefined} onClick={() => setHelperId(p.contactId)}>
                  {p.displayName}
                </button>
              ))}
              {people.data && people.data.items.length === 0 && <span className={`${s.small} ${s.muted}`}>No one found.</span>}
            </div>
            <Field label="Rating" hint="Five stars lets the helper's owner know it went well.">
              <div className={s.stars} role="radiogroup" aria-label="Rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n === 1 ? "" : "s"}`} className={s.star} data-on={rating !== null && n <= rating ? "true" : "false"} onClick={() => setRating(n as 1 | 2 | 3 | 4 | 5)}>
                    <Star size={16} fill={rating !== null && n <= rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
            </Field>
          </>
        )}
        <Field label="Notes" hint="Optional. Kept on your side only.">
          <Textarea value={review} onChange={(e) => setReview(e.target.value)} rows={3} aria-label="Review" />
        </Field>
      </div>
    </Modal>
  );
}
